import React, { useCallback, useState } from 'react';
import { Transition, Link, Heading } from '@printdeal/ui-library';
import clsx from 'clsx';
import { addDataLayerEntry } from '@printdeal/data-layer-manager';
import { FormattedMessage } from 'react-intl';
import type { Navigation, NavigationItem } from '../../../types';
import NavigationLink from '../NavigationLink';

type MobileAllCategoriesNavigationItemProps = {
  item: NavigationItem,
};

const MobileAllCategoriesNavigationItem = ({ item }: MobileAllCategoriesNavigationItemProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = useCallback(() => {
    if (!isOpen) {
      // GA4.
      addDataLayerEntry('navigation_main_mobile_show', {
        label: 'Level2',
      });
    }

    setIsOpen(!isOpen);
  }, [isOpen]);

  return (
    <div className="pd-flex pd-flex-col pd-gap-3">
      <div className="pd-flex pd-gap-3">
        <NavigationLink
          to={item.linkedEntry?.path}
          iconBefore={item.icon}
          onClick={handleToggle}
          navigationType="main"
          navigationLevel="main_Level1"
          eventLabel={item.title}
          className={clsx(isOpen && 'pd-text-primary-500')}
        >
          {item.title}
        </NavigationLink>
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <Link
          as="button"
          type="button"
          variant="secondary"
          iconBefore={isOpen ? 'chevron-up' : 'chevron-down'}
          onClick={handleToggle}
          className="pd-ml-auto"
        >
          <span className="pd-sr-only">
            <FormattedMessage id="navigation.submenu.toggle" defaultMessage="Toggle menu" />
          </span>
        </Link>
      </div>
      <Transition show={isOpen} variant="appear-from-top" className="pd-pb-3">
        <ul className="pd-flex pd-flex-col pd-gap-4" data-testid="mobile-all-categories-navigation-submenu">
          {item.collapsibleContent?.map((collapsibleContent) => {
            if (!collapsibleContent.title) return null;

            return (
              <li key={collapsibleContent.id} data-level="2" className="pd-flex pd-flex-col pd-gap-2">
                <NavigationLink
                  to={collapsibleContent.linkedEntry?.path}
                  navigationType="main"
                  navigationLevel="level2"
                  eventLabel={collapsibleContent.title}
                >
                  {collapsibleContent.title}
                </NavigationLink>
                <ul className="pd-flex pd-flex-col pd-gap-1 pd-pl-3 pd-text-sm">
                  {collapsibleContent.subItems?.map(({
                    linkedEntry, path, title, id,
                  }) => {
                    if (!linkedEntry && !path) {
                      return null;
                    }
                    const navSubItemTitle = linkedEntry ? linkedEntry.title : title;
                    return (
                      <li key={id} data-level="3">
                        <NavigationLink
                          to={linkedEntry ? linkedEntry.path : path}
                          navigationType="main"
                          navigationLevel="level3"
                          eventLabel={navSubItemTitle}
                          className="pd-text-sm"
                        >
                          {navSubItemTitle}
                        </NavigationLink>
                      </li>
                    );
                  })}
                </ul>
              </li>
            );
          })}
        </ul>
      </Transition>
    </div>
  );
};

type MobileAllCategoriesNavigationProps = {
  navigation: Navigation;
  className?: string;
};

const MobileAllCategoriesNavigation = ({
  navigation,
  className,
}: MobileAllCategoriesNavigationProps) => (
  <nav className={clsx('pd-flex pd-flex-col pd-gap-4', className)} aria-labelledby="mobile-all-categories-navigation-title">
    <Heading variant="display" as="span" size="3" id="mobile-all-categories-navigation-title">
      {navigation.title}
    </Heading>
    <ul className="pd-flex pd-flex-col pd-gap-3">
      {navigation.items?.map((navigationItem) => (
        <li key={navigationItem.id} data-level="1">
          <MobileAllCategoriesNavigationItem item={navigationItem} />
        </li>
      ))}
    </ul>
  </nav>
);

export default MobileAllCategoriesNavigation;
